import {
  effect,
  type EffectRef,
  inject,
  Injectable,
  Injector,
  signal,
  untracked,
} from '@angular/core';
import { Router } from '@angular/router';
import { CurrentAccountService } from './current-account.service';
import { resolveLandingRoute } from './resolve-landing-route';

/**
 * One-shot instruction the resolver raises for the sidebar, consumed by `AppSidebar` (the lazy
 * admin layout owns `HlmSidebarService`; `@aurora` imports no Spartan UI library — HR-PORTS-001).
 *
 * - `collapse`: the session hides the vertical navigation; force it closed.
 * - `restore`: a PREVIOUS materialization forced a collapse that was never the user's own choice,
 *   and this one does not ask for it — hand back the state the user was in before that force, so a
 *   forced collapse does not linger in the 7-day `sidebar_state` cookie across an unrelated later
 *   session (e.g. an operator who impersonates a hidden-nav account and then ends it).
 */
export type SidebarIntent = { kind: 'collapse' } | { kind: 'restore'; open: boolean } | null;

/** How long `apply()` waits for the account load before giving up and navigating anyway. */
export const LANDING_WAIT_TIMEOUT_MS = 15_000;

/**
 * Single entry point for the three session-materialization points (design.md Decision 1):
 * successful local sign-in, and both impersonation transitions through
 * `AuthenticationAuroraAdapterService#reMaterializeSession`. Deliberately NOT invoked on cold boot,
 * a transparent token refresh, or guard-driven recovery — those must never pull the user away from
 * the route they requested (capability `frontend-role-ui-preferences`).
 *
 * Depends only on the `CurrentAccountService` PORT and `Router` — HR-PORTS-001.
 */
@Injectable({ providedIn: 'root' })
export class LandingResolverService {
  private readonly currentAccount = inject(CurrentAccountService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  private readonly sidebarIntentSig = signal<SidebarIntent>(null);
  readonly sidebarIntent = this.sidebarIntentSig.asReadonly();

  /**
   * The sidebar's own `open` state the moment a FORCED `collapse` was applied, recorded by
   * `acknowledgeSidebarIntent({ previousOpen })`. `null` means "no forced collapse is currently
   * pending restoration". Kept across materializations (in memory only — a cold boot starts clean,
   * which is acceptable: see design.md Decision 4) so a later materialization that does NOT ask
   * for a collapse can hand the user's own preference back instead of leaving the force in place.
   */
  private openStateBeforeForcedCollapse: boolean | null = null;

  private pendingWait: EffectRef | null = null;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Waits for the CURRENT account load to settle, then raises the sidebar intent (if any) and
   * navigates to the resolved landing route.
   *
   * Re-arms the account's readiness latch itself — `apply()` is the ONE place that decides when a
   * new wait begins, so a stale `true` left over from a PREVIOUS materialization can never
   * short-circuit it: `load()` runs FIRST, synchronously, before anything waits on `loaded()`.
   *
   * The wait is a fresh, self-destroying `effect()` created per call against this service's OWN
   * injector — deliberately NOT `toObservable(...)`, which registers its internal watcher on the
   * ROOT `DestroyRef` (it takes `manualCleanup: true`), so a per-call `toObservable` instance would
   * leak one live watcher for the rest of the app's lifetime on every materialization; an `effect()`
   * destroys its own watcher the moment it fires.
   *
   * A load that never settles (a stalled network request — `catchError` only sees EMITTED errors,
   * not a request that simply hangs) would otherwise strand the user on the screen that started the
   * session, with an impersonation credential already swapped in. `LANDING_WAIT_TIMEOUT_MS` bounds
   * the wait: past it, the resolver gives up, navigates using ONLY the explicit redirect (or `/`) —
   * never a role's `defaultRedirection`, which it cannot trust without a settled load — and raises
   * no sidebar intent, since it has no idea whether the session hides the navigation.
   *
   * @param explicitRedirect - The raw `?redirectURL=` value the flow was bounced with (unvalidated
   *   — `resolveLandingRoute` decides), or `null` when the materialization has none (both
   *   impersonation transitions).
   */
  apply(explicitRedirect: string | null): void {
    this.awaitLanding(explicitRedirect, (route) => void this.router.navigateByUrl(route));
  }

  /**
   * Same wait, sidebar intent and timeout as `apply()`, but hands the landing route back instead
   * of navigating — for a caller that is itself inside a navigation (a route guard) and must
   * return the route as its redirect: navigating on the side cancels the guard's navigation, and
   * a landing equal to the current URL is then ignored as a same-URL navigation.
   *
   * A later `resolve()`/`apply()` supersedes this one: the promise then never settles, which is
   * harmless for a guard because the router has already abandoned its navigation.
   */
  resolve(explicitRedirect: string | null): Promise<string> {
    return new Promise((resolve) => this.awaitLanding(explicitRedirect, resolve));
  }

  private awaitLanding(explicitRedirect: string | null, onLanding: (route: string) => void): void {
    // A previous wait might still be pending (e.g. a second materialization request arrives
    // before the first settled) — cancel it so at most one landing is ever delivered per call.
    this.cancelPendingWait();

    // Clear any intent a PREVIOUS materialization raised but nothing ever consumed — e.g. a role
    // redirection that left the admin layout entirely (`/signed-out`), so no `AppSidebar` instance
    // was ever mounted to acknowledge it. Left uncleared, it would leak into whichever `AppSidebar`
    // mounts next, forcing a collapse no CURRENT role asked for.
    this.sidebarIntentSig.set(null);

    this.currentAccount.load();

    const ref = effect(
      () => {
        if (!this.currentAccount.loaded()) return;
        this.settle(ref, explicitRedirect, false, onLanding);
      },
      { injector: this.injector },
    );
    this.pendingWait = ref;
    this.pendingTimeout = setTimeout(() => {
      this.settle(ref, explicitRedirect, true, onLanding);
    }, LANDING_WAIT_TIMEOUT_MS);
  }

  /**
   * Records the sidebar's state as it was just before a `collapse` was applied (once — a later
   * ack while a snapshot is already pending is ignored, so an already-forced `false` never
   * overwrites the user's REAL original preference), and clears the current intent either way.
   */
  acknowledgeSidebarIntent(ack?: { previousOpen: boolean }): void {
    if (ack && this.openStateBeforeForcedCollapse === null) {
      this.openStateBeforeForcedCollapse = ack.previousOpen;
    }
    this.sidebarIntentSig.set(null);
  }

  private cancelPendingWait(): void {
    this.pendingWait?.destroy();
    this.pendingWait = null;
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  /** Settles the current wait exactly once — via the effect firing OR the timeout, whichever first. */
  private settle(
    ref: EffectRef,
    explicitRedirect: string | null,
    timedOut: boolean,
    onLanding: (route: string) => void,
  ): void {
    ref.destroy();
    this.pendingWait = null;
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }

    untracked(() => {
      if (!timedOut) {
        if (this.currentAccount.hasHiddenVerticalNavigation()) {
          this.sidebarIntentSig.set({ kind: 'collapse' });
        } else if (this.openStateBeforeForcedCollapse !== null) {
          this.sidebarIntentSig.set({ kind: 'restore', open: this.openStateBeforeForcedCollapse });
          this.openStateBeforeForcedCollapse = null;
        }
      }
      const route = resolveLandingRoute({
        explicitRedirect,
        defaultRedirection: timedOut ? null : this.currentAccount.defaultRedirection(),
      });
      onLanding(route);
    });
  }
}
